import {
    useEffect,
    useState,
} from "react";

import CrudModal from "components/crud/CrudModal";
import CrudInput from "components/crud/CrudInput";
import CrudSelect from "components/crud/CrudSelect";

import {
    createStatement,
} from "services/api/reportsApi";

import {
    showSuccess,
    showError,
} from "utils/toast";


const INITIAL_FORM = {
    statementType: "party",
    entityId: "",
    fromDate: "",
    toDate: "",
    format: "pdf",
};


export default function StatementModal({
    open,
    onClose,
    reload,
}) {

    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        form,
        setForm,
    ] = useState(
        INITIAL_FORM
    );


    /*
    |--------------------------------------------------------------------------
    | RESET
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (!open) {
            return;
        }

        setForm(
            INITIAL_FORM
        );

    }, [open]);


    /*
    |--------------------------------------------------------------------------
    | CHANGE
    |--------------------------------------------------------------------------
    */

    const handleChange = (
        e
    ) => {

        const {
            name,
            value,
        } = e.target;


        setForm(
            (prev) => ({
                ...prev,
                [name]: value,
            })
        );
    };


    /*
    |--------------------------------------------------------------------------
    | SUBMIT
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (
        e
    ) => {

        e.preventDefault();


        if (!form.entityId) {

            showError(
                "Please enter Entity ID."
            );

            return;
        }


        if (
            form.fromDate &&
            form.toDate &&
            form.fromDate >
                form.toDate
        ) {

            showError(
                "From Date cannot be greater than To Date."
            );

            return;
        }


        try {

            setLoading(
                true
            );


            const payload = {
                statementType:
                    form.statementType,

                entityId:
                    form.entityId,

                fromDate:
                    form.fromDate ||
                    undefined,

                toDate:
                    form.toDate ||
                    undefined,

                format:
                    form.format,
            };


            await createStatement(
                payload
            );


            showSuccess(
                "Statement generated successfully."
            );


            if (
                typeof reload ===
                "function"
            ) {
                reload();
            }


            onClose();

        } catch (
            error
        ) {

            console.error(
                "Statement generation error:",
                error
            );


            showError(
                error?.response?.data?.message ||
                "Unable to generate statement."
            );

        } finally {

            setLoading(
                false
            );
        }
    };


    if (!open) {
        return null;
    }


    return (

        <CrudModal

            open={open}

            title="Generate Statement"

            loading={loading}

            submitText="Generate"

            onClose={onClose}

            onSubmit={
                handleSubmit
            }

            width="max-w-2xl"
        >

            <div
                className="
                    grid
                    grid-cols-1
                    gap-4
                    md:grid-cols-2
                "
            >

                {/* =====================================================
                    STATEMENT TYPE
                ===================================================== */}

                <CrudSelect

                    label="Statement Type"

                    name="statementType"

                    value={
                        form.statementType
                    }

                    onChange={
                        handleChange
                    }

                    options={[
                        {
                            value: "party",
                            label: "Party Statement",
                        },
                        {
                            value: "school",
                            label: "School Statement",
                        },
                        {
                            value: "employee",
                            label: "Employee Statement",
                        },
                        {
                            value: "collection",
                            label: "Collection Statement",
                        },
                        {
                            value: "payment",
                            label: "Payment Statement",
                        },
                    ]}
                />


                {/* =====================================================
                    ENTITY ID
                ===================================================== */}

                <CrudInput

                    label="Entity ID"

                    name="entityId"

                    value={
                        form.entityId
                    }

                    onChange={
                        handleChange
                    }

                    placeholder="Enter ID"

                    required
                />


                {/* =====================================================
                    FROM DATE
                ===================================================== */}

                <CrudInput

                    label="From Date"

                    type="date"

                    name="fromDate"

                    value={
                        form.fromDate
                    }

                    onChange={
                        handleChange
                    }
                />


                {/* =====================================================
                    TO DATE
                ===================================================== */}

                <CrudInput

                    label="To Date"

                    type="date"

                    name="toDate"

                    value={
                        form.toDate
                    }

                    onChange={
                        handleChange
                    }
                />


                {/* =====================================================
                    FORMAT
                ===================================================== */}

                <CrudSelect

                    label="Format"

                    name="format"

                    value={
                        form.format
                    }

                    onChange={
                        handleChange
                    }

                    options={[
                        {
                            value: "pdf",
                            label: "PDF",
                        },
                        {
                            value: "xlsx",
                            label: "Excel",
                        },
                        {
                            value: "csv",
                            label: "CSV",
                        },
                    ]}
                />

            </div>


            {/* =========================================================
                INFO
            ========================================================= */}

            <div
                className="
                    mt-5
                    rounded-lg
                    border
                    border-blue-200
                    bg-blue-50
                    p-4
                    text-sm
                    text-blue-700
                "
            >
                <p className="font-semibold">
                    Statement generation
                </p>

                <p className="mt-1">
                    Select the statement type,
                    enter the related Entity ID,
                    choose the date range and
                    output format.
                </p>
            </div>

        </CrudModal>
    );
}